// src/components/common/CurrentLocationCard.js
//
// Displays the GPS-detected "Current Location" address with an Edit toggle
// so the customer can correct the text (apartment/floor number, a missing
// landmark, a mis-detected area name, etc.) without touching the underlying
// lat/lng/geo_location — those stay exactly as GPS-detected and are what
// actually gets saved with the order (see CurrentLocationDetector.js +
// _place_order_core's geo_location handling on the backend).
//
// City and Area are fixed, config-driven selections (CONFIG.ACTIVE_CITIES/
// CITIES, CONFIG.AREA_OPTIONS) — never free text — so an edited address
// always carries the same canonical city slug partner-matching compares
// against, instead of whatever spelling Google's reverse geocoder returned
// (see the city-alias bug this fixed on the backend:
// special_service_assign_common.py's normalize_city()).

import React, { useState } from "react";
import CONFIG from "../../config";

const CITY_OPTIONS = (CONFIG.ACTIVE_CITIES || []).map((slug) => ({
  value: slug, label: CONFIG.CITIES?.[slug]?.label || slug,
}));

// Google's Geocoder returns a city name ("Bengaluru", "Bangalore"), not our
// slug ("bengaluru") — match it against the configured list (slug or label,
// case-insensitive) so the City dropdown opens pre-selected on the right
// option instead of blank. Falls back to "" (forces an explicit pick) when
// the detected city isn't one of our serviceable cities at all.
const normalizeCityToSlug = (value) => {
  const v = String(value || "").trim().toLowerCase();
  if (!v) return "";
  const bySlug = (CONFIG.ACTIVE_CITIES || []).find((slug) => slug.toLowerCase() === v);
  if (bySlug) return bySlug;
  const byLabel = (CONFIG.ACTIVE_CITIES || []).find(
    (slug) => (CONFIG.CITIES?.[slug]?.label || "").toLowerCase() === v
  );
  return byLabel || "";
};

const areaOptionsForCity = (citySlug) => {
  const entry = CONFIG.AREA_OPTIONS?.[citySlug] || CONFIG.AREA_OPTIONS?.[citySlug?.toLowerCase()];
  return entry?.areas || [];
};

const inputStyle = { width:"100%", padding:"8px 10px", borderRadius:"8px", border:"1px solid #cbd5e1", fontSize:"13.5px", boxSizing:"border-box" };
const labelStyle = { fontSize:"11.5px", fontWeight:700, color:"#64748b", display:"block", marginBottom:"4px" };
const editBtnStyle = { border:"1px solid #c7d2fe", background:"#fff", color:"#4338ca", fontWeight:700, fontSize:"12px", borderRadius:"8px", padding:"5px 10px", cursor:"pointer" };
const saveBtnStyle = { ...editBtnStyle, background:"#4338ca", color:"#fff", border:"1px solid #4338ca" };

const CurrentLocationCard = ({
  addr, onChange,
  cardStyle  = { border:"1px solid #e2e8f0", borderRadius:"16px", padding:"16px", background:"#fbfdff", display:"flex", flexDirection:"column", gap:"12px" },
  badgeStyle = { width:"fit-content", padding:"5px 10px", borderRadius:"999px", background:"#eef2ff", color:"#4338ca", fontSize:"13px", fontWeight:800 },
  blockStyle = { fontSize:"14px", color:"#334155", lineHeight:1.8 },
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(addr || {});

  if (!addr) return null;

  const startEdit = () => { setDraft({ ...addr, city: normalizeCityToSlug(addr.city) }); setEditing(true); };
  const cancel    = () => setEditing(false);
  const save = () => {
    onChange({
      ...addr,
      line1: draft.line1 || "", area: draft.area || "", city: draft.city || "",
      state: draft.state || "", pincode: draft.pincode || "",
      // The Google-formatted string no longer matches once edited — clear it
      // so display falls back to joining the (now edited) fields below.
      formatted_address: "",
      // lat / lng / geo_location intentionally left untouched — these are
      // the real GPS coordinates and must reach the backend as detected,
      // regardless of any text correction made here.
    });
    setEditing(false);
  };

  const cityLabel = CONFIG.CITIES?.[addr.city]?.label || addr.city;
  const addressText = addr.formatted_address ||
    [addr.line1, addr.area, cityLabel, addr.state, addr.pincode].filter(Boolean).join(", ");

  return (
    <div style={cardStyle}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
        <span style={badgeStyle}>🛰 Current Location</span>
        {!editing && <button type="button" onClick={startEdit} style={editBtnStyle}>✏️ Edit</button>}
      </div>

      {!editing ? (
        <div style={blockStyle}>{addressText}</div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          <div>
            <label style={labelStyle}>Street / Line 1</label>
            <input style={inputStyle} value={draft.line1 || ""} placeholder="House no., Street name"
              onChange={e => setDraft(d => ({ ...d, line1: e.target.value }))} />
          </div>
          <div>
            <label style={labelStyle}>City</label>
            <select style={inputStyle} value={draft.city || ""}
              onChange={e => setDraft(d => ({ ...d, city: e.target.value, area: "" }))}>
              <option value="">Select city</option>
              {CITY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Area</label>
            <select style={inputStyle} value={draft.area || ""}
              onChange={e => setDraft(d => ({ ...d, area: e.target.value }))}>
              <option value="">Select area</option>
              {areaOptionsForCity(draft.city).map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>State</label>
            <input style={inputStyle} value={draft.state || ""} placeholder="State"
              onChange={e => setDraft(d => ({ ...d, state: e.target.value }))} />
          </div>
          <div>
            <label style={labelStyle}>Pincode</label>
            <input style={inputStyle} value={draft.pincode || ""} placeholder="6-digit pincode"
              onChange={e => setDraft(d => ({ ...d, pincode: e.target.value }))} />
          </div>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button type="button" onClick={cancel} style={editBtnStyle}>Cancel</button>
            <button type="button" onClick={save} style={saveBtnStyle}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentLocationCard;
